/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { WorkflowFrequency } from "~/generated/graphql";
import { dateFormat } from "~/constants";

export const enum WorkflowFields {
  NAME = "name",
  DESCRIPTION = "description",
  CONTACT_PERSON_IDS = "contactPersonIds",
  SCHEDULER = "scheduler",
  SERVICE_NOW = "serviceNow",
  EMAIL = "email",
  ACTIVE = "active",
  TRIGGER_COMPONENTS = "triggerComponents",
  SELECT_DATA_COMPONENT = "selectDataComponent",
  SEND_DATA_COMPONENTS = "sendDataComponents",
  SCHEDULER_SUBMISSION_SNAPSHOT = "schedulerSubmissionSnapshot",
  SERVICE_NOW_SUBMISSION_SNAPSHOT = "serviceNowSubmissionSnapshot",
  EMAIL_SUBMISSION_SNAPSHOT = "emailSubmissionSnapshot",
  SLACK = "slack",
  SLACK_SUBMISSION_SNAPSHOT = "slackSubmissionSnapshot",
  SAVINGS_SUGGESTIONS = "savingsSuggestions",
  KPI_OPTIONS = "kpiOptions",
  COSTS_OPTIONS = "costsOptions",
  BUDGETS_OPTIONS = "budgetsOptions",
  BUDGETS_SNAPSHOT = "budgetsSubmissionSnapshot",
  ANOMALY_COSTS_OPTIONS = "anomalyCostsOptions",
  ANOMALY_SNAPSHOT = "anomalyCostsOptionsSubmissionSnapshot",
}

export const enum SchedulerFields {
  START_DATE = "startDate",
  EXECUTION_TIME = "executionTime",
  FREQUENCY = "frequency",
  INTERVAL = "interval",
  WEEKDAYS = "weekdays",
  END_DATE = "endDate",
  REPEAT = "repeat",
}

export const enum ServiceNowFields {
  URL = "url",
  USERNAME = "username",
  PASSWORD = "password",
  SHORT_DESCRIPTION = "shortDescription",
  DESCRIPTION = "description",
  ADDITIONAL_MESSAGE_FIELDS = "additionalMessageFields",
  COSTS_OPTIONS = "costsOptions",
  KPI_OPTIONS = "kpiOptions",
  SAVING_SUGGESTIONS_OPTIONS = "savingSuggestionsOptions",
}

export const enum EmailFields {
  SEND_TO_CONTACT_PERSONS = "sendToContactPersons",
  SEND_TO_ADDITIONAL_USERS = "sendToAdditionalUsers",
  MAILTO = "mailTo",
  SUBJECT = "subject",
  MESSAGE = "message",
  MESSAGE_TYPE = "messageType",
}

export const enum SlackFields {
  TOKEN = "token",
  CHANNEL = "channel",
  MESSAGE = "message",
  COSTS_OPTIONS = "costsOptions",
  KPI_OPTIONS = "kpiOptions",
  SAVING_SUGGESTIONS_OPTIONS = "savingSuggestionsOptions",
}

export const enum SlackCostsOptions {
  TOTAL_MONTHLY_COST = "totalMonthlyCost",
  TOTAL_MONTHLY_FORECAST = "totalMonthlyForecast",
  COSTS_BY_PROVIDERS = "costsByProviders",
  COSTS_BY_CATEGORIES = "costsByCategories",
}

export const enum SlackKpiOptions {
  COST_ALLOCATION = "costAllocation",
  OVERALL_WASTE = "overallWaste",
  NON_PROP_HOURS = "nonProdHours",
}

export const enum SlackSavingSuggestionsOptions {
  TOTAL_MONTHLY_SAVINGS = "totalMonthlySavings",
  MONTHLY_SAVINGS_BY_PROVIDERS = "monthlySavingsByProviders",
  MONTHLY_SAVINGS_BY_CATEGORIES = "monthlySavingsByCategories",
  MONTHLY_SAVINGS_BY_POLICIES = "monthlySavingsByPolicies",
}

export const WeekdayLabelMap: { [key: number]: string } = {
  0: "MON",
  1: "TUE",
  2: "WED",
  3: "THU",
  4: "FRI",
  5: "SAT",
  6: "SUN",
};

export const FrequencyTextMap = {
  [WorkflowFrequency.Daily]: "day",
  [WorkflowFrequency.Weekly]: "week",
  [WorkflowFrequency.Monthly]: "month",
  [WorkflowFrequency.Yearly]: "year",
};

export const enum Step {
  DETAILS = 0,
  SETTINGS = 1,
  SUMMARY = 2,
}

export const tomorrowsDate = dayjs().add(1, "day").format(dateFormat.shortDate);

export const SHORT_DESCRIPTION_LIMIT = 160;
